import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { backoffMs, clearOps, listOps, newOpId, putOp, removeOp } from "@/lib/offlineQueue";

const OfflineContext = createContext(null);

const isNetworkError = (error) => !error?.response;

/**
 * Queues site updates while the connection is down and replays them exactly once.
 * Server-side idempotency keys make retries safe; 409 responses become user conflicts.
 */
export function OfflineProvider({ children }) {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [ops, setOps] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const flushing = useRef(false);

  const reload = useCallback(async () => {
    setOps(await listOps());
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const enqueue = useCallback(
    async (request) => {
      const op = {
        op_id: newOpId(),
        method: request.method,
        path: request.path,
        body: request.body ?? null,
        label: request.label,
        panel_uid: request.panel_uid || null,
        version: request.version || null,
        created_at: new Date().toISOString(),
        attempts: 0,
        next_attempt_at: new Date().toISOString(),
        state: "pending",
        error: null,
        server: null,
      };
      await putOp(op);
      await reload();
      return op;
    },
    [reload]
  );

  /** Sends one queued op; returns true when it can be dropped from the queue. */
  const push = useCallback(async (op, { force = false } = {}) => {
    const headers = {
      "X-Idempotency-Key": op.op_id,
      "X-Client-At": op.created_at,
      "X-Offline-Sync": "1",
    };
    if (op.version && !force) headers["X-If-Unchanged-Since"] = op.version;
    if (force) headers["X-Force-Overwrite"] = "1";
    try {
      await api.request({
        method: op.method,
        url: op.path,
        data: op.body ?? undefined,
        headers,
      });
      await removeOp(op.op_id);
      return true;
    } catch (error) {
      if (isNetworkError(error)) {
        const attempts = op.attempts + 1;
        await putOp({
          ...op,
          attempts,
          state: "pending",
          error: "Connection unavailable",
          next_attempt_at: new Date(Date.now() + backoffMs(attempts)).toISOString(),
        });
        return false;
      }
      if (error.response.status === 409) {
        const detail = error.response.data?.detail;
        const server = detail?.server ?? error.response.data?.server ?? null;
        await putOp({
          ...op,
          state: server ? "conflict" : "failed",
          error: apiError(error),
          server,
        });
        return false;
      }
      await putOp({ ...op, state: "failed", error: apiError(error) });
      return false;
    }
  }, []);

  const flush = useCallback(
    async () => {
      if (flushing.current || !navigator.onLine) return;
      flushing.current = true;
      setSyncing(true);
      try {
        const queued = await listOps();
        const due = queued.filter(
          (op) => op.state === "pending" && op.next_attempt_at <= new Date().toISOString()
        );
        let sent = 0;
        for (const op of due) {
          // eslint-disable-next-line no-await-in-loop
          if (await push(op)) sent += 1;
        }
        await reload();
        if (sent) toast.success(`${sent} pending change(s) synchronized`);
      } finally {
        flushing.current = false;
        setSyncing(false);
      }
    },
    [push, reload]
  );

  /** Tries the call immediately; falls back to the durable queue when offline. */
  const submit = useCallback(
    async ({ method, path, body, label, panel_uid, version }) => {
      const clientAt = new Date().toISOString();
      const opId = newOpId();
      if (navigator.onLine) {
        try {
          const response = await api.request({
            method,
            url: path,
            data: body,
            headers: { "X-Idempotency-Key": opId, "X-Client-At": clientAt },
          });
          return { ok: true, queued: false, data: response.data };
        } catch (error) {
          if (!isNetworkError(error)) return { ok: false, queued: false, error: apiError(error) };
        }
      }
      await enqueue({ method, path, body, label, panel_uid, version });
      return { ok: true, queued: true };
    },
    [enqueue]
  );

  const resolveConflict = useCallback(
    async (opId, choice) => {
      const queued = await listOps();
      const op = queued.find((row) => row.op_id === opId);
      if (!op) return;
      if (choice === "server") {
        await removeOp(opId);
        toast.success("Server version kept");
      } else if (await push({ ...op, state: "pending" }, { force: true })) {
        toast.success("Your change was reapplied");
      } else {
        toast.error("Could not reapply the change — it stays in the queue");
      }
      await reload();
    },
    [push, reload]
  );

  const retryFailed = useCallback(async () => {
    const queued = await listOps();
    await Promise.all(
      queued
        .filter((op) => op.state === "failed")
        .map((op) =>
          putOp({ ...op, state: "pending", attempts: 0, error: null,
            next_attempt_at: new Date().toISOString() })
        )
    );
    await reload();
    await flush();
  }, [flush, reload]);

  const discard = useCallback(
    async (opId) => {
      await removeOp(opId);
      await reload();
    },
    [reload]
  );

  const clearAll = useCallback(async () => {
    await clearOps();
    setOps([]);
  }, []);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      flush();
    };
    const goOffline = () => setOnline(false);
    const wake = () => {
      if (document.visibilityState === "visible") flush();
    };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    window.addEventListener("focus", wake);
    document.addEventListener("visibilitychange", wake);
    const timer = setInterval(() => flush(), 30000);
    flush();
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("focus", wake);
      document.removeEventListener("visibilitychange", wake);
      clearInterval(timer);
    };
  }, [flush]);

  const pending = ops.filter((op) => op.state === "pending");
  const failed = ops.filter((op) => op.state === "failed");
  const conflicts = ops.filter((op) => op.state === "conflict");

  const status = !online
    ? "offline"
    : failed.length || conflicts.length
      ? "failed"
      : syncing && pending.length
        ? "syncing"
        : pending.length
          ? "pending"
          : "online";

  const isPendingPanel = useCallback(
    (panelUid) => ops.some((op) => op.panel_uid === panelUid && op.state === "pending"),
    [ops]
  );

  const value = useMemo(
    () => ({
      online,
      status,
      ops,
      pending,
      failed,
      conflicts,
      syncing,
      submit,
      flush,
      retryFailed,
      resolveConflict,
      discard,
      clearAll,
      isPendingPanel,
    }),
    [
      online,
      status,
      ops,
      pending,
      failed,
      conflicts,
      syncing,
      submit,
      flush,
      retryFailed,
      resolveConflict,
      discard,
      clearAll,
      isPendingPanel,
    ]
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export const useOffline = () => useContext(OfflineContext);
